import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { AutoComplete } from 'primereact/autocomplete';
import { ConfirmDialog } from 'primereact/confirmdialog';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import { toastify } from '../../../utils/toast';
import { makeRequest2 } from '../../../utils/makeRequest';
import Layout from '../component/layout';
import { countries } from '../../../utils/data';
import ConfirmDeleteDialog from '../component/confirmDialog';
import { getFacilitiesURL } from '../../../utils/urls';

function PowerMeterConfigs() {
  // State for manufacturers
  const [manufacturers, setManufacturers] = useState([]);
  const [manufacturerName, setManufacturerName] = useState('');

  // State for customer bands
  const [customerBands, setCustomerBands] = useState([]);
  const [newCustomerBand, setNewCustomerBand] = useState('');

  // State for protocols
  const [protocols, setProtocols] = useState([]);
  const [newProtocol, setNewProtocol] = useState('');

  // State for gateways
  const [gateways, setGateways] = useState([]);
  const [gatewayForm, setGatewayForm] = useState({
    facilityId: '',
    gatewayName: '',
    gatewayId: '',
    manufacturer: '',
    location: '',
    type: '',
    status: ''
  });
  const [facilities, setFacilities] = useState([]);

  const [loading, setLoading] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  // Base URLs
  const manufacturerBaseUrl = '/api/core/power_meter_management/controllers/manufacturers';
  const customerBandBaseUrl = '/api/core/power_meter_management/controllers/customer_band';
  const protocolBaseUrl = '/api/core/power_meter_management/controllers/protocols';
  const gatewayBaseUrl = '/api/core/power_meter_management/controllers/gateways';
  const facilitiesBaseUrl = getFacilitiesURL;

  // Gateway type options
  const gatewayTypes = [
    { value: 'LAN', label: 'LAN' },
    { value: 'IOT', label: 'IOT' }
  ];

  // Gateway status options
  const gatewayStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'maintenance', label: 'Maintenance' }
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = () => {
    fetchManufacturers();
    fetchCustomerBands();
    fetchProtocols();
    fetchGateways();
    fetchFacilities();
  };

  const fetchManufacturers = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(`${manufacturerBaseUrl}/get_all`, 'GET');

      if (response.data.data && Array.isArray(response.data.data)) {
        setManufacturers(response.data.data);
      } else {
        toastify(response.error || 'Failed to fetch manufacturers', 'error');
        setManufacturers([]);
      }
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
      toastify('An error occurred while fetching manufacturers', 'error');
      setManufacturers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerBands = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(`${customerBandBaseUrl}/get_all`, 'GET');

      if (response.data.data && Array.isArray(response.data.data)) {
        setCustomerBands(response.data.data);
      } else {
        toastify(response.error || 'Failed to fetch customer bands', 'error');
        setCustomerBands([]);
      }
    } catch (error) {
      console.error('Error fetching customer bands:', error);
      toastify('An error occurred while fetching customer bands', 'error');
      setCustomerBands([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProtocols = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(`${protocolBaseUrl}/get_all_protocols`, 'GET');

      if (response.data.data && Array.isArray(response.data.data)) {
        setProtocols(response.data.data);
      } else {
        toastify(response.error || 'Failed to fetch protocols', 'error');
        setProtocols([]);
      }
    } catch (error) {
      console.error('Error fetching protocols:', error);
      toastify('An error occurred while fetching protocols', 'error');
      setProtocols([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGateways = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(`${gatewayBaseUrl}/get_all_gateways`, 'GET');

      if (response.data.data && Array.isArray(response.data.data)) {
        setGateways(response.data.data);
      } else {
        toastify(response.error || 'Failed to fetch gateways', 'error');
        setGateways([]);
      }
    } catch (error) {
      console.error('Error fetching gateways:', error);
      toastify('An error occurred while fetching gateways', 'error');
      setGateways([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await makeRequest2(facilitiesBaseUrl, 'GET');
      
      if (response && response.success) {
        let facilitiesData = [];
        
        if (Array.isArray(response.data)) {
          facilitiesData = response.data;
        } else if (response.data && Array.isArray(response.data.facilities)) {
          facilitiesData = response.data.facilities;
        } else if (response.facilities && Array.isArray(response.facilities)) {
          facilitiesData = response.facilities;
        }
        
        setFacilities(facilitiesData);
      } else {
        setFacilities([]);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setFacilities([]);
    }
  };

  // Handler functions for manufacturers
  const handleAddManufacturer = async () => {
    if (!manufacturerName.trim()) {
      toastify('Please enter a manufacturer name', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await makeRequest2(`${manufacturerBaseUrl}/add_manufacturer`, 'POST', {
        name: manufacturerName.trim()
      });

      if (response.success || response.message) {
        toastify('Manufacturer added successfully', 'success');
        setManufacturerName('');
        fetchManufacturers();
      } else {
        toastify(response.error || 'Failed to add manufacturer', 'error');
      }
    } catch (error) {
      console.error('Error adding manufacturer:', error);
      toastify('Error adding manufacturer', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteManufacturer = (manufacturer) => {
    setItemToDelete(manufacturer);
    setDeleteType('manufacturer');
    setDeleteDialogVisible(true);
  };

  // Handler functions for customer bands
  const handleAddCustomerBand = async () => {
    if (!newCustomerBand.trim()) {
      toastify('Please enter a customer band name', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await makeRequest2(`${customerBandBaseUrl}/add_customerband`, 'POST', {
        name: newCustomerBand.trim()
      });

      if (response.success || response.message) {
        toastify('Customer band added successfully', 'success');
        setNewCustomerBand('');
        fetchCustomerBands();
      } else {
        toastify(response.error || 'Failed to add customer band', 'error');
      }
    } catch (error) {
      console.error('Error adding customer band:', error);
      toastify('Error adding customer band', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomerBand = (band) => {
    setItemToDelete(band);
    setDeleteType('customerBand');
    setDeleteDialogVisible(true);
  };

  // Handler functions for protocols
  const handleAddProtocol = async () => {
    if (!newProtocol.trim()) {
      toastify('Please enter a protocol name', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await makeRequest2(`${protocolBaseUrl}/add_protocol`, 'POST', {
        name: newProtocol.trim()
      });

      if (response.success || response.message) {
        toastify('Protocol added successfully', 'success');
        setNewProtocol('');
        fetchProtocols();
      } else {
        toastify(response.error || 'Failed to add protocol', 'error');
      }
    } catch (error) {
      console.error('Error adding protocol:', error);
      toastify('Error adding protocol', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProtocol = (protocol) => {
    setItemToDelete(protocol);
    setDeleteType('protocol');
    setDeleteDialogVisible(true);
  };

  // Handler functions for gateways
  const handleGatewayFormChange = (field, value) => {
    setGatewayForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddGateway = async () => {
    // Validation
    if (!gatewayForm.facilityId.trim()) {
      toastify('Please select a facility', 'error');
      return;
    }
    if (!gatewayForm.gatewayName.trim()) {
      toastify('Please enter a gateway name', 'error');
      return;
    }
    if (!gatewayForm.manufacturer.trim()) {
      toastify('Please select a manufacturer', 'error');
      return;
    }
    if (!gatewayForm.type.trim()) {
      toastify('Please select a gateway type', 'error');
      return;
    }
    if (!gatewayForm.status.trim()) {
      toastify('Please select a status', 'error');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        facilityId: gatewayForm.facilityId,
        gatewayName: gatewayForm.gatewayName.trim(),
        gatewayId: gatewayForm.gatewayId ? parseInt(gatewayForm.gatewayId) : undefined,
        manufacturer: gatewayForm.manufacturer,
        location: gatewayForm.location.trim() || undefined,
        type: gatewayForm.type,
        status: gatewayForm.status
      };

      const response = await makeRequest2(`${gatewayBaseUrl}/add_gateway`, 'POST', payload);

      if (response.success || response.message) {
        toastify('Gateway added successfully', 'success');
        setGatewayForm({
          facilityId: '',
          gatewayName: '',
          gatewayId: '',
          manufacturer: '',
          location: '',
          type: '',
          status: ''
        });
        fetchGateways();
      } else {
        toastify(response.error || 'Failed to add gateway', 'error');
      }
    } catch (error) {
      console.error('Error adding gateway:', error);
      toastify('Error adding gateway', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGateway = (gateway) => {
    setItemToDelete(gateway);
    setDeleteType('gateway');
    setDeleteDialogVisible(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      let response;

      switch (deleteType) {
        case 'manufacturer':
          response = await makeRequest2(`${manufacturerBaseUrl}/delete_manufacturer/${itemToDelete._id}`, 'DELETE');
          if (response.success || response.message) {
            toastify('Manufacturer deleted successfully', 'success');
            fetchManufacturers();
          } else {
            toastify(response.error || 'Failed to delete manufacturer', 'error');
          }
          break;

        case 'customerBand':
          response = await makeRequest2(`${customerBandBaseUrl}/delete_customerband/${itemToDelete._id}`, 'DELETE');
          if (response.success || response.message) {
            toastify('Customer band deleted successfully', 'success');
            fetchCustomerBands();
          } else {
            toastify(response.error || 'Failed to delete customer band', 'error');
          }
          break;

        case 'protocol':
          response = await makeRequest2(`${protocolBaseUrl}/delete_protocol/${itemToDelete._id}`, 'DELETE');
          if (response.success || response.message) {
            toastify('Protocol deleted successfully', 'success');
            fetchProtocols();
          } else {
            toastify(response.error || 'Failed to delete protocol', 'error');
          }
          break;

        case 'gateway':
          response = await makeRequest2(`${gatewayBaseUrl}/delete_gateway/${itemToDelete._id}`, 'DELETE');
          if (response.success || response.message) {
            toastify('Gateway deleted successfully', 'success');
            fetchGateways();
          } else {
            toastify(response.error || 'Failed to delete gateway', 'error');
          }
          break;

        default:
          toastify('Invalid delete operation', 'error');
          break;
      }
    } catch (error) {
      console.error(`Error deleting ${deleteType}:`, error);
      toastify(`Error deleting ${deleteType}`, 'error');
    } finally {
      setLoading(false);
      setDeleteDialogVisible(false);
      setItemToDelete(null);
      setDeleteType('');
    }
  };

  const getDeleteMessage = () => {
    switch (deleteType) {
      case 'manufacturer':
        return `Are you sure you want to delete the manufacturer "${itemToDelete?.name}"?`;
      case 'customerBand':
        return `Are you sure you want to delete the customer band "${itemToDelete?.name}"?`;
      case 'protocol':
        return `Are you sure you want to delete the protocol "${itemToDelete?.name}"?`;
      case 'gateway':
        return `Are you sure you want to delete the gateway "${itemToDelete?.gatewayName}"?`;
      default:
        return 'Are you sure you want to delete this item?';
    }
  };

  const getFacilityName = (facilityId) => {
    const facility = facilities.find(f => f._id === facilityId);
    return facility ? facility.name : 'Unknown Facility';
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/core/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Power Meter Management</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Configs</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card mb-4">
            <div className="card-header p-3">
              <div className="card-body py-0">
                <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                  <li className="nav-item">
                    <a className="nav-link active" id="profile-tab-1" data-bs-toggle="tab" href="#profile-1" role="tab" aria-selected="true">
                      <i className="ti ti-building me-2"></i>
                      <span>Meter Manufacturers</span>
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" id="profile-tab-2" data-bs-toggle="tab" href="#profile-2" role="tab" aria-selected="false">
                      <i className="ti ti-users me-2"></i>
                      <span>Customer Bands</span>
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" id="profile-tab-3" data-bs-toggle="tab" href="#profile-3" role="tab" aria-selected="false">
                      <i className="ti ti-brand-producthunt me-2"></i>
                      <span>Meter Communication Protocols</span>
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link" id="profile-tab-4" data-bs-toggle="tab" href="#profile-4" role="tab" aria-selected="false">
                      <i className="ti ti-router me-2"></i>
                      <span>Power Meter Gateways</span>
                    </a>
                  </li>
                </ul>
              </div>

              <div className="tab-content">
                {/* Manufacturers Tab */}
                <div className="tab-pane show active" id="profile-1" role="tabpanel">
                  <div className="row g-4 p-4">
                    <div className="col-md-12 mb-3">
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-12">
                                <div>
                                  <label>Manufacturer Name<span style={{ color: 'red' }}>*</span></label>
                                  <input
                                    type="text"
                                    className="form-control mt-2"
                                    placeholder="Enter Manufacturer Name"
                                    value={manufacturerName}
                                    onChange={(e) => setManufacturerName(e.target.value)}
                                    disabled={loading}
                                  />
                                </div>

                                <div className="mt-3" style={{ float: 'right' }}>
                                  <button
                                    className="btn btn-primary"
                                    onClick={handleAddManufacturer}
                                    disabled={loading || !manufacturerName.trim()}
                                  >
                                    {loading ? 'Adding...' : 'Submit'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-body">
                            {loading ? (
                              <div className="text-center p-3">Loading...</div>
                            ) : (
                              <ul className="list-group">
                                {manufacturers.length === 0 ? (
                                  <li className="list-group-item text-center">No manufacturers found</li>
                                ) : (
                                  manufacturers.map((manufacturer, index) => (
                                    <li className="list-group-item" key={manufacturer._id}>
                                      <span>
                                        {index + 1}. {manufacturer.name}
                                        <div style={{ float: 'right', color: 'red' }}>
                                          <Link
                                            to="#"
                                            style={{ color: 'red' }}
                                            onClick={() => handleDeleteManufacturer(manufacturer)}
                                          >
                                            <i className="ti ti-trash"></i>
                                          </Link>
                                        </div>
                                      </span>
                                    </li>
                                  ))
                                )}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Bands Tab */}
                <div className="tab-pane" id="profile-2" role="tabpanel">
                  <div className="row g-4 p-4">
                    <div className="col-md-12 mb-3">
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-12">
                                <label>Customer Band<span style={{ color: 'red' }}>*</span></label>
                                <input
                                  className="form-control mt-2"
                                  placeholder="Enter Customer Band e.g Residential, Commercial"
                                  value={newCustomerBand}
                                  onChange={(e) => setNewCustomerBand(e.target.value)}
                                  disabled={loading}
                                />
                                <div className="mt-3" style={{ float: 'right' }}>
                                  <button
                                    className="btn btn-primary"
                                    onClick={handleAddCustomerBand}
                                    disabled={loading || !newCustomerBand.trim()}
                                  >
                                    {loading ? 'Adding...' : 'Submit'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-body">
                            {loading ? (
                              <div className="text-center p-3">Loading...</div>
                            ) : (
                              <ul className="list-group">
                                {customerBands.length === 0 ? (
                                  <li className="list-group-item text-center">No customer bands found</li>
                                ) : (
                                  customerBands.map((band, index) => (
                                    <li className="list-group-item" key={band._id}>
                                      <span>
                                        {index + 1}. {band.name}
                                        <div style={{ float: 'right', color: 'red' }}>
                                          <Link
                                            to="#"
                                            style={{ color: 'red' }}
                                            onClick={() => handleDeleteCustomerBand(band)}
                                          >
                                            <i className="ti ti-trash"></i>
                                          </Link>
                                        </div>
                                      </span>
                                    </li>
                                  ))
                                )}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Protocols Tab */}
                <div className="tab-pane" id="profile-3" role="tabpanel">
                  <div className="row g-4 p-4">
                    <div className="col-md-12 mb-3">
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-12">
                                <label>Communication Protocol<span style={{ color: 'red' }}>*</span></label>
                                <input
                                  className="form-control mt-2"
                                  placeholder="Enter Power Meter Communication Protocol e.g RS485, Modbus, TCP"
                                  value={newProtocol}
                                  onChange={(e) => setNewProtocol(e.target.value)}
                                  disabled={loading}
                                />
                                <div className="mt-3" style={{ float: 'right' }}>
                                  <button
                                    className="btn btn-primary"
                                    onClick={handleAddProtocol}
                                    disabled={loading || !newProtocol.trim()}
                                  >
                                    {loading ? 'Adding...' : 'Submit'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-body">
                            {loading ? (
                              <div className="text-center p-3">Loading...</div>
                            ) : (
                              <ul className="list-group">
                                {protocols.length === 0 ? (
                                  <li className="list-group-item text-center">No protocols found</li>
                                ) : (
                                  protocols.map((protocol, index) => (
                                    <li className="list-group-item" key={protocol._id}>
                                      <span>
                                        {index + 1}. {protocol.name}
                                        <div style={{ float: 'right', color: 'red' }}>
                                          <Link
                                            to="#"
                                            style={{ color: 'red' }}
                                            onClick={() => handleDeleteProtocol(protocol)}
                                          >
                                            <i className="ti ti-trash"></i>
                                          </Link>
                                        </div>
                                      </span>
                                    </li>
                                  ))
                                )}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gateways Tab */}
                <div className="tab-pane" id="profile-4" role="tabpanel">
                  <div className="row g-4 p-4">
                    <div className="col-md-12 mb-3">
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-12">
                                {/* Facility Selection */}
                                <div className="mb-3">
                                  <label>Facility<span style={{ color: 'red' }}>*</span></label>
                                  <select
                                    className="form-control mt-2"
                                    value={gatewayForm.facilityId}
                                    onChange={(e) => handleGatewayFormChange('facilityId', e.target.value)}
                                    disabled={loading}
                                  >
                                    <option value="">Select Facility</option>
                                    {facilities.map((facility) => (
                                      <option key={facility._id} value={facility._id}>
                                        {facility.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Gateway Name */}
                                <div className="mb-3">
                                  <label>Gateway Name<span style={{ color: 'red' }}>*</span></label>
                                  <input
                                    type="text"
                                    className="form-control mt-2"
                                    placeholder="Enter Gateway Name"
                                    value={gatewayForm.gatewayName}
                                    onChange={(e) => handleGatewayFormChange('gatewayName', e.target.value)}
                                    disabled={loading}
                                  />
                                </div>

                                {/* Gateway ID */}
                                <div className="mb-3">
                                  <label>Gateway ID (Optional)</label>
                                  <input
                                    type="number"
                                    className="form-control mt-2"
                                    placeholder="Enter Gateway ID"
                                    value={gatewayForm.gatewayId}
                                    onChange={(e) => handleGatewayFormChange('gatewayId', e.target.value)}
                                    disabled={loading}
                                  />
                                </div>

                                {/* Manufacturer Selection */}
                                <div className="mb-3">
                                  <label>Manufacturer<span style={{ color: 'red' }}>*</span></label>
                                  <select
                                    className="form-control mt-2"
                                    value={gatewayForm.manufacturer}
                                    onChange={(e) => handleGatewayFormChange('manufacturer', e.target.value)}
                                    disabled={loading}
                                  >
                                    <option value="">Select Manufacturer</option>
                                    {manufacturers.map((mfr) => (
                                      <option key={mfr._id} value={mfr.name}>
                                        {mfr.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Location */}
                                <div className="mb-3">
                                  <label>Location</label>
                                  <input
                                    type="text"
                                    className="form-control mt-2"
                                    placeholder="Enter Location"
                                    value={gatewayForm.location}
                                    onChange={(e) => handleGatewayFormChange('location', e.target.value)}
                                    disabled={loading}
                                  />
                                </div>

                                {/* Gateway Type */}
                                <div className="mb-3">
                                  <label>Gateway Type<span style={{ color: 'red' }}>*</span></label>
                                  <select
                                    className="form-control mt-2"
                                    value={gatewayForm.type}
                                    onChange={(e) => handleGatewayFormChange('type', e.target.value)}
                                    disabled={loading}
                                  >
                                    <option value="">Select Gateway Type</option>
                                    {gatewayTypes.map((type) => (
                                      <option key={type.value} value={type.value}>
                                        {type.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Gateway Status */}
                                <div className="mb-3">
                                  <label>Status<span style={{ color: 'red' }}>*</span></label>
                                  <select
                                    className="form-control mt-2"
                                    value={gatewayForm.status}
                                    onChange={(e) => handleGatewayFormChange('status', e.target.value)}
                                    disabled={loading}
                                  >
                                    <option value="">Select Status</option>
                                    {gatewayStatusOptions.map((status) => (
                                      <option key={status.value} value={status.value}>
                                        {status.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="mt-3" style={{ float: 'right' }}>
                                  <button
                                    className="btn btn-outline-secondary me-2"
                                    onClick={() => setGatewayForm({
                                      facilityId: '',
                                      gatewayName: '',
                                      gatewayId: '',
                                      manufacturer: '',
                                      location: '',
                                      type: '',
                                      status: ''
                                    })}
                                    disabled={loading}
                                  >
                                    Clear
                                  </button>
                                  <button
                                    className="btn btn-primary"
                                    onClick={handleAddGateway}
                                    disabled={loading || !gatewayForm.facilityId || !gatewayForm.gatewayName.trim() || !gatewayForm.manufacturer || !gatewayForm.type || !gatewayForm.status}
                                  >
                                    {loading ? 'Adding...' : 'Submit'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-body">
                            <div className="card-header p-0 mb-3">
                              <h6>Gateway List</h6>
                            </div>
                            {loading ? (
                              <div className="text-center p-3">Loading...</div>
                            ) : (
                              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {gateways.length === 0 ? (
                                  <div className="text-center p-3">No gateways found</div>
                                ) : (
                                  gateways.map((gateway, index) => (
                                    <div className="card mb-2" key={gateway._id}>
                                      <div className="card-body p-3">
                                        <div className="d-flex justify-content-between align-items-start">
                                          <div>
                                            <h6 className="mb-1">{gateway.gatewayName}</h6>
                                            <small className="text-muted">
                                              <strong>Facility:</strong> {getFacilityName(gateway.facilityId)}<br />
                                              <strong>ID:</strong> {gateway.gatewayId || 'N/A'}<br />
                                              <strong>Manufacturer:</strong> {gateway.manufacturer}<br />
                                              <strong>Type:</strong> {gateway.type}<br />
                                              <strong>Status:</strong> 
                                              <span className={`badge ms-1 ${
                                                gateway.status === 'active' ? 'bg-success' :
                                                gateway.status === 'inactive' ? 'bg-secondary' :
                                                'bg-warning'
                                              }`}>
                                                {gateway.status}
                                              </span><br />
                                              {gateway.location && (
                                                <>
                                                  <strong>Location:</strong> {gateway.location}
                                                </>
                                              )}
                                            </small>
                                          </div>
                                          <div>
                                            <Link
                                              to="#"
                                              style={{ color: 'red' }}
                                              onClick={() => handleDeleteGateway(gateway)}
                                            >
                                              <i className="ti ti-trash"></i>
                                            </Link>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add the ConfirmDeleteDialog */}
      <ConfirmDeleteDialog
        visible={deleteDialogVisible}
        onHide={() => {
          setDeleteDialogVisible(false);
          setItemToDelete(null);
          setDeleteType('');
        }}
        message={getDeleteMessage()}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDialog />
    </Layout>
  );
}

export default PowerMeterConfigs;